import { useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SpineTemplate } from "../../../types/spine-template";
import { TemplateItem } from "./TemplateItem";
import { SortableRelatedTemplateItem } from "./SortableRelatedTemplateItem";
import {
  PENDING_RELATED_TYPE_KEY,
  bucketRelatedTemplateIdsByType,
  flattenRelatedTemplateGroups,
} from "./relatedTemplateOrder";

interface SelectedTemplatesListProps {
  selectedTemplateIds: string[];
  templateCache: Map<string, Partial<SpineTemplate>>;
  onRemoveTemplate: (templateId: string) => void;
  onReorderTemplates: (nextIds: string[]) => void;
}

function typeSectionLabel(typeKey: string): string {
  if (typeKey === PENDING_RELATED_TYPE_KEY) return "待识别类型";
  return typeKey;
}

export function SelectedTemplatesList({
  selectedTemplateIds,
  templateCache,
  onRemoveTemplate,
  onReorderTemplates,
}: SelectedTemplatesListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const groups = useMemo(
    () =>
      bucketRelatedTemplateIdsByType(selectedTemplateIds, templateCache),
    [selectedTemplateIds, templateCache],
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const gIdx = groups.findIndex((g) => g.ids.includes(activeId));
    if (gIdx === -1) return;

    const g = groups[gIdx];
    if (!g.ids.includes(overId)) return;

    const oldIndex = g.ids.indexOf(activeId);
    const newIndex = g.ids.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1) return;

    const nextIds = arrayMove(g.ids, oldIndex, newIndex);
    const nextGroups = groups.map((gr, i) =>
      i === gIdx ? { ...gr, ids: nextIds } : gr,
    );
    onReorderTemplates(flattenRelatedTemplateGroups(nextGroups));
  };

  return (
    <Card className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden shadow-none">
      <CardHeader className="shrink-0 p-3">
        <CardTitle className="flex items-center justify-between text-sm">
          <span>已选中的模板</span>
          {selectedTemplateIds.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {selectedTemplateIds.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-3 pt-0">
        <div className="min-h-0 flex-1 overflow-y-auto">
          {selectedTemplateIds.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              暂无选中的模板
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <div className="space-y-4 pr-1">
                {groups.map((group) => (
                  <div key={group.typeKey} className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2 px-0.5">
                      <span className="text-xs font-semibold text-muted-foreground">
                        {typeSectionLabel(group.typeKey)}
                      </span>
                      <Badge variant="outline" className="h-4 px-1 text-[10px]">
                        {group.ids.length}
                      </Badge>
                      {group.ids.length > 1 && (
                        <span className="text-[10px] text-muted-foreground">
                          同类型可拖拽，越靠下层级越高
                        </span>
                      )}
                    </div>
                    {group.ids.length === 1 ? (
                      <TemplateItem
                        key={group.ids[0]}
                        template={
                          templateCache.get(group.ids[0]) || {
                            id: group.ids[0],
                          }
                        }
                        onRemove={() => onRemoveTemplate(group.ids[0])}
                        showAction
                        isSelected
                      />
                    ) : (
                      <SortableContext
                        items={group.ids}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2">
                          {group.ids.map((id) => (
                            <SortableRelatedTemplateItem
                              key={id}
                              id={id}
                              template={templateCache.get(id) || { id }}
                              onRemove={() => onRemoveTemplate(id)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    )}
                  </div>
                ))}
              </div>
            </DndContext>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
